/**
 * TerrainHydrology — drainage analysis for heightfields: priority-flood
 * depression filling (Barnes, Lehman & Mulla 2014), D8 steepest-descent flow
 * routing, O(n log n) flow accumulation, river extraction, moisture fields
 * and an RGBA flow-map export for water-surface currents.
 *
 * All functions are pure TypeScript over row-major `resolution × resolution`
 * Float32 heightfields in world units — no Three.js, unit-testable in Node,
 * and mirrorable server-side. The stream-power eroder (TerrainErosion.ts)
 * builds directly on this analysis.
 */

import { activeHydrologyKernel } from './HydrologyKernel.js';

/** 8-neighbour offsets: E, W, S, N, SE, SW, NE, NW (cardinals first). */
const NX = [1, -1, 0, 0, 1, -1, 1, -1] as const;
const NZ = [0, 0, 1, -1, 1, 1, -1, -1] as const;
const SQRT2 = Math.SQRT2;

/** Binary min-heap over cell indices keyed by an external priority array. */
class MinHeap {
  private readonly items: Int32Array;
  private readonly keys: Float64Array;
  private count = 0;

  constructor(capacity: number) {
    this.items = new Int32Array(capacity);
    this.keys = new Float64Array(capacity);
  }

  get size(): number {
    return this.count;
  }

  push(index: number, key: number): void {
    let i = this.count++;
    this.items[i] = index;
    this.keys[i] = key;
    while (i > 0) {
      const parent = (i - 1) >> 1;
      if (this.keys[parent] <= this.keys[i]) break;
      this.swap(i, parent);
      i = parent;
    }
  }

  /** Pop the lowest-keyed cell index. */
  pop(): number {
    const top = this.items[0];
    this.count--;
    if (this.count > 0) {
      this.items[0] = this.items[this.count];
      this.keys[0] = this.keys[this.count];
      let i = 0;
      for (;;) {
        const l = i * 2 + 1;
        const r = l + 1;
        let smallest = i;
        if (l < this.count && this.keys[l] < this.keys[smallest]) smallest = l;
        if (r < this.count && this.keys[r] < this.keys[smallest]) smallest = r;
        if (smallest === i) break;
        this.swap(i, smallest);
        i = smallest;
      }
    }
    return top;
  }

  private swap(a: number, b: number): void {
    const ti = this.items[a];
    this.items[a] = this.items[b];
    this.items[b] = ti;
    const tk = this.keys[a];
    this.keys[a] = this.keys[b];
    this.keys[b] = tk;
  }
}

/**
 * Priority-flood depression filling: raise every interior pit/flat until it
 * drains to the map edge, with an `epsilon` gradient so the filled surface
 * has NO flats — every cell gains a strictly downhill D8 path to the border.
 * Returns a NEW array; the input is untouched.
 */
export function fillDepressions(height: Float32Array, resolution: number, epsilon = 1e-4): Float32Array {
  // Not routed to the wasm kernel on its own (1.3× — see HydrologyKernel.ts);
  // analyzeHydrology runs the fused kernel pipeline instead.
  const n = resolution;
  const out = new Float32Array(height);
  const closed = new Uint8Array(n * n);
  const heap = new MinHeap(n * n);

  // Seed the flood with the border cells (they always drain off-map).
  for (let i = 0; i < n; i++) {
    const cells = [i, (n - 1) * n + i, i * n, i * n + (n - 1)];
    for (const c of cells) {
      if (!closed[c]) {
        closed[c] = 1;
        heap.push(c, out[c]);
      }
    }
  }

  while (heap.size > 0) {
    const c = heap.pop();
    const cx = c % n;
    const cz = (c / n) | 0;
    for (let k = 0; k < 8; k++) {
      const x = cx + NX[k];
      const z = cz + NZ[k];
      if (x < 0 || x >= n || z < 0 || z >= n) continue;
      const ni = z * n + x;
      if (closed[ni]) continue;
      closed[ni] = 1;
      // Raise pits/flats just above the spill cell so water keeps moving.
      if (out[ni] <= out[c]) out[ni] = out[c] + epsilon;
      heap.push(ni, out[ni]);
    }
  }
  return out;
}

/**
 * D8 steepest-descent receivers: for each cell, the index of the neighbour
 * with the steepest downhill drop (diagonals weighted by 1/√2), or −1 when
 * no neighbour is lower (border outlets; interior pits unless the field was
 * depression-filled first).
 */
export function computeFlowDirections(height: Float32Array, resolution: number): Int32Array {
  // Wasm kernel when installed (bit-identical; see HydrologyKernel.ts).
  const kernel = activeHydrologyKernel();
  if (kernel) {
    const out = kernel.computeFlowDirections(height, resolution);
    if (out) return out;
  }
  const n = resolution;
  const receivers = new Int32Array(n * n).fill(-1);
  for (let z = 0; z < n; z++) {
    for (let x = 0; x < n; x++) {
      const i = z * n + x;
      const h = height[i];
      let best = -1;
      let bestDrop = 0;
      for (let k = 0; k < 8; k++) {
        const nx = x + NX[k];
        const nz = z + NZ[k];
        if (nx < 0 || nx >= n || nz < 0 || nz >= n) continue;
        const ni = nz * n + nx;
        const drop = (h - height[ni]) / (k < 4 ? 1 : SQRT2);
        if (drop > bestDrop) {
          bestDrop = drop;
          best = ni;
        }
      }
      receivers[i] = best;
    }
  }
  return receivers;
}

/**
 * Cell indices ordered by DESCENDING height — processing in this order
 * guarantees every donor is visited before its receiver.
 */
export function orderByDescendingHeight(height: Float32Array, resolution: number): Int32Array {
  const kernel = activeHydrologyKernel();
  if (kernel) {
    const out = kernel.orderByDescendingHeight(height, resolution);
    if (out) return out;
  }
  const n = resolution * resolution;
  const order = new Int32Array(n);
  for (let i = 0; i < n; i++) order[i] = i;
  // Typed-array sort is implementation-stable enough for our use: ties are
  // broken by index via the subtraction fallback, keeping runs deterministic.
  return order.sort((a, b) => height[b] - height[a] || a - b);
}

/**
 * Flow accumulation: how many cells drain THROUGH each cell (including
 * itself), in cell counts. Multiply by cellArea for physical drainage area.
 */
export function computeFlowAccumulation(
  height: Float32Array,
  receivers: Int32Array,
  resolution: number,
  order?: Int32Array,
): Float32Array {
  const n = resolution * resolution;
  const acc = new Float32Array(n).fill(1);
  const ord = order ?? orderByDescendingHeight(height, resolution);
  for (let k = 0; k < n; k++) {
    const i = ord[k];
    const r = receivers[i];
    if (r >= 0) acc[r] += acc[i];
  }
  return acc;
}

export interface HydrologyResult {
  /** Depression-filled surface (world units). */
  filled: Float32Array;
  /** D8 receiver index per cell (−1 = border outlet). */
  receivers: Int32Array;
  /** Cells draining through each cell (≥ 1). */
  accumulation: Float32Array;
  /** Cell indices by descending filled height (donors before receivers). */
  order: Int32Array;
}

/** One-call drainage analysis: fill → route → accumulate. */
export function analyzeHydrology(height: Float32Array, resolution: number, epsilon = 1e-4): HydrologyResult {
  // Fused wasm pipeline when installed — one boundary crossing, bit-identical.
  const kernel = activeHydrologyKernel();
  if (kernel) {
    const out = kernel.analyzeHydrology(height, resolution, epsilon);
    if (out) return out;
  }
  const filled = fillDepressions(height, resolution, epsilon);
  const receivers = computeFlowDirections(filled, resolution);
  const order = orderByDescendingHeight(filled, resolution);
  const accumulation = computeFlowAccumulation(filled, receivers, resolution, order);
  return { filled, receivers, accumulation, order };
}

/**
 * River mask: 1 where accumulation exceeds `thresholdCells` (a stream has
 * gathered at least that much upstream area), else 0.
 */
export function extractRivers(accumulation: Float32Array, thresholdCells: number): Uint8Array {
  const mask = new Uint8Array(accumulation.length);
  for (let i = 0; i < accumulation.length; i++) {
    if (accumulation[i] >= thresholdCells) mask[i] = 1;
  }
  return mask;
}

export interface MoistureConfig {
  /** Cells at/below this height are open water (sea/lakes). Default −Infinity (none). */
  waterLevel?: number;
  /** River threshold in cells (see extractRivers). Default 2% of the map. */
  riverThresholdCells?: number;
  /** e-folding distance of the moisture falloff, in CELLS. Default 12. */
  falloffCells?: number;
}

/**
 * Moisture field in [0,1]: 1 at open water and rivers, decaying
 * exponentially with chamfer distance from the nearest water source.
 * Drives biome/splat decisions (wet → grass/forest, dry → sand/rock).
 */
export function computeMoisture(
  height: Float32Array,
  resolution: number,
  accumulation: Float32Array,
  config: MoistureConfig = {},
): Float32Array {
  const n = resolution;
  const total = n * n;
  const waterLevel = config.waterLevel ?? -Infinity;
  const riverThreshold = config.riverThresholdCells ?? total * 0.02;
  const falloff = Math.max(1e-3, config.falloffCells ?? 12);

  // Two-pass 3×3 chamfer distance transform (exact enough for a falloff).
  const INF = 1e9;
  const dist = new Float32Array(total).fill(INF);
  for (let i = 0; i < total; i++) {
    if (height[i] <= waterLevel || accumulation[i] >= riverThreshold) dist[i] = 0;
  }
  // Forward pass (W, N, NW, NE).
  for (let z = 0; z < n; z++) {
    for (let x = 0; x < n; x++) {
      const i = z * n + x;
      let d = dist[i];
      if (x > 0) d = Math.min(d, dist[i - 1] + 1);
      if (z > 0) {
        d = Math.min(d, dist[i - n] + 1);
        if (x > 0) d = Math.min(d, dist[i - n - 1] + SQRT2);
        if (x < n - 1) d = Math.min(d, dist[i - n + 1] + SQRT2);
      }
      dist[i] = d;
    }
  }
  // Backward pass (E, S, SE, SW).
  for (let z = n - 1; z >= 0; z--) {
    for (let x = n - 1; x >= 0; x--) {
      const i = z * n + x;
      let d = dist[i];
      if (x < n - 1) d = Math.min(d, dist[i + 1] + 1);
      if (z < n - 1) {
        d = Math.min(d, dist[i + n] + 1);
        if (x < n - 1) d = Math.min(d, dist[i + n + 1] + SQRT2);
        if (x > 0) d = Math.min(d, dist[i + n - 1] + SQRT2);
      }
      dist[i] = d;
    }
  }

  const moisture = new Float32Array(total);
  for (let i = 0; i < total; i++) {
    moisture[i] = Math.exp(-dist[i] / falloff);
  }
  return moisture;
}

/**
 * RGBA flow-map data for GPU use (RG = flow direction ×0.5+0.5, B = log-
 * normalized accumulation, A = 1). Feed to a DataTexture to advect water
 * normals/foam along terrain runoff, or to visualize drainage.
 */
export function buildFlowMapData(
  receivers: Int32Array,
  accumulation: Float32Array,
  resolution: number,
): Float32Array {
  const n = resolution;
  const total = n * n;
  const data = new Float32Array(total * 4);
  // log1p compresses the huge accumulation dynamic range (1 .. n²).
  const logMax = Math.log1p(total);
  for (let i = 0; i < total; i++) {
    const r = receivers[i];
    let dx = 0;
    let dz = 0;
    if (r >= 0) {
      dx = (r % n) - (i % n);
      dz = ((r / n) | 0) - ((i / n) | 0);
      const len = Math.sqrt(dx * dx + dz * dz);
      if (len > 0) {
        dx /= len;
        dz /= len;
      }
    }
    data[i * 4] = dx * 0.5 + 0.5;
    data[i * 4 + 1] = dz * 0.5 + 0.5;
    data[i * 4 + 2] = Math.log1p(accumulation[i]) / logMax;
    data[i * 4 + 3] = 1;
  }
  return data;
}
